import Spinner from '@components/spinner/Spinner';
import { Close, DragIndicator, MicOffOutlined, Videocam } from '@mui/icons-material';
import { Alert, Box, IconButton, Tooltip, Typography } from '@mui/material';
import { mediaType } from '@utils/constants';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import ReactPlayer from 'react-player';

export const RecordingFile = ({
  playing,
  setPlayedSeconds,
  mediaData,
  isLoadingVideo,
  setPlaying,
  useAspectRatio = true,
  aspectRatio = '16/9',
  ref,
}) => {
  const { ref: viewRef, inView } = useInView({ threshold: 0.1 });

  // Sticky video state
  const [isStickyClosed, setIsStickyClosed] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: 20 }); // bottom-right offset
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });

  // Reset sticky closed state when transcript changes
  useEffect(() => {
    setIsStickyClosed(false);
    setPosition({ x: 20, y: 20 });
  }, [mediaData?.blob_storage_url]);

  // Drag handlers
  const handleMouseDown = useCallback(
    (e) => {
      if (e.target.closest('.drag-handle')) {
        setIsDragging(true);
        dragStartRef.current = {
          x: e.clientX,
          y: e.clientY,
          posX: position.x,
          posY: position.y,
        };
        e.preventDefault();
      }
    },
    [position],
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;

      const deltaX = dragStartRef.current.x - e.clientX;
      const deltaY = dragStartRef.current.y - e.clientY;

      const newX = Math.max(
        0,
        Math.min(window.innerWidth - 340, dragStartRef.current.posX + deltaX),
      );
      const newY = Math.max(
        0,
        Math.min(window.innerHeight - 200, dragStartRef.current.posY + deltaY),
      );

      setPosition({ x: newX, y: newY });
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Show sticky video only when scrolled out of view and not manually closed
  const showSticky = !inView && !isStickyClosed;

  if (isLoadingVideo) return <Spinner />;

  // No recording found - subtle inline message
  if (!mediaData?.blob_storage_url || mediaData?.media_type === 'text') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1.5,
          px: 2,
          mt: 1,
          borderRadius: 1,
          bgcolor: 'grey.50',
          border: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <MicOffOutlined sx={{ fontSize: 20, color: 'grey.500' }} />
        <Typography variant="body2" color="text.secondary">
          No audio or video recording associated with this transcript.
        </Typography>
      </Box>
    );
  }

  // Check if video is being processed
  const isProcessing = mediaData?.duration_fixing === true;
  const needsProcessing = mediaData?.duration_fixed === false && !isProcessing;

  return (
    <>
      {/* Video processing alert */}
      {(isProcessing || needsProcessing) && (
        <Alert severity="info" sx={{ mb: 2, mt: 1 }} icon={<Videocam />}>
          {isProcessing
            ? 'Video is being optimized for playback. Seeking may not work until processing completes.'
            : 'Video duration metadata is being fixed. This may take a minute.'}
        </Alert>
      )}

      {/* Show video button when sticky is closed */}
      {!inView && isStickyClosed && (
        <Tooltip title="Show video player">
          <IconButton
            onClick={() => setIsStickyClosed(false)}
            sx={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              zIndex: 999999999,
              backgroundColor: 'primary.main',
              color: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            <Videocam />
          </IconButton>
        </Tooltip>
      )}

      <div ref={viewRef}>
        {mediaData.media_type === mediaType.audio ? (
          // Audio player
          <audio
            ref={ref}
            src={mediaData.blob_storage_url}
            controls
            style={{
              width: showSticky ? 400 : '100%',
              background: '#f2f2f2',
              borderRadius: 8,
              boxShadow: showSticky ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
              position: showSticky ? 'fixed' : 'static',
              bottom: showSticky ? position.y : undefined,
              right: showSticky ? position.x : undefined,
              zIndex: showSticky ? 999999999 : 1,
            }}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            onTimeUpdate={(e) => setPlayedSeconds(e.currentTarget.currentTime)}
          >
            <track kind="captions" src="" srcLang="en" label="English" default />
          </audio>
        ) : (
          // Video player with sticky container
          <>
            {/* Main video (visible when in view) */}
            {inView &&
              (useAspectRatio ? (
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: aspectRatio,
                    backgroundColor: '#000',
                  }}
                >
                  <ReactPlayer
                    ref={ref}
                    url={mediaData.blob_storage_url}
                    controls
                    playing={playing}
                    onProgress={({ playedSeconds }) => setPlayedSeconds(playedSeconds)}
                    onPlay={() => setPlaying(true)}
                    onPause={() => setPlaying(false)}
                    onEnded={() => setPlaying(false)}
                    width="100%"
                    height="100%"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      background: '#000',
                    }}
                  />
                </Box>
              ) : (
                <ReactPlayer
                  ref={ref}
                  url={mediaData.blob_storage_url}
                  controls
                  playing={playing}
                  onProgress={({ playedSeconds }) => setPlayedSeconds(playedSeconds)}
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  onEnded={() => setPlaying(false)}
                  width="100%"
                  height={300}
                  style={{
                    background: '#000',
                  }}
                />
              ))}

            {/* Sticky video container (visible when scrolled and not closed) */}
            {showSticky && (
              <Box
                ref={dragRef}
                onMouseDown={handleMouseDown}
                sx={{
                  position: 'fixed',
                  bottom: position.y,
                  right: position.x,
                  zIndex: 999999999,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: '#000',
                  cursor: isDragging ? 'grabbing' : 'default',
                  userSelect: 'none',
                }}
              >
                {/* Control bar */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '4px 8px',
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
                    zIndex: 10,
                  }}
                >
                  {/* Drag handle */}
                  <Tooltip title="Drag to move">
                    <IconButton
                      className="drag-handle"
                      size="small"
                      sx={{
                        color: 'white',
                        cursor: 'grab',
                        '&:active': { cursor: 'grabbing' },
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                      }}
                    >
                      <DragIndicator fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {/* Close button */}
                  <Tooltip title="Close mini player">
                    <IconButton
                      onClick={() => setIsStickyClosed(true)}
                      size="small"
                      sx={{
                        color: 'white',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <ReactPlayer
                  ref={!inView ? ref : undefined}
                  url={mediaData.blob_storage_url}
                  controls
                  playing={playing}
                  onProgress={({ playedSeconds }) => setPlayedSeconds(playedSeconds)}
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  onEnded={() => setPlaying(false)}
                  width={320}
                  height={180}
                  style={{
                    pointerEvents: 'auto',
                  }}
                />
              </Box>
            )}
          </>
        )}
      </div>
    </>
  );
};
